import { withWindow } from '@shared/utils/withWindow';

const localeToCurrency: Record<typeof navigator.language, String> = {
  'en-US': 'USD',
  'en-GB': 'GBP',
  'en-CA': 'CAD',
  'fr-CA': 'CAD',
  'fr-FR': 'EUR',
  'de-DE': 'EUR',
  'es-ES': 'EUR',
  'ja-JP': 'JPY',
  'zh-CN': 'CNY',
  'ko-KR': 'KRW',
  'pt-BR': 'BRL',
  'ru-RU': 'RUB',
  'hi-IN': 'INR',
  'en-AU': 'AUD',
  'en-NZ': 'NZD',
  'en-IN': 'INR',
  'en-ZA': 'ZAR',
  'ar-AE': 'AED',
  'en-FJ': 'FJD',
  'en-SG': 'SGD',
  'en-PH': 'PHP',
  'en-MY': 'MYR',
  'en-OM': 'OMR',
  'en-SA': 'SAR',
  'en-HK': 'HKD',
  'en-MU': 'MUR',
  'en-MX': 'MXN',
  'en-LK': 'LKR',
  'en-MN': 'MNT',
  'en-MA': 'MAD',
  'en-ID': 'IDR',
  'en-TH': 'THB',
  'en-TW': 'TWD',
  'en-JE': 'GBP',
  'en-GG': 'GBP',
  'en-IM': 'GBP',
  'en-IE': 'EUR',
  'en-NF': 'AUD',
  'en-TV': 'AUD',
  'en-KI': 'AUD',
  'en-NR': 'AUD',
  'en-CC': 'AUD',
  'en-CX': 'AUD',
  'en-HM': 'AUD',
  'en-PT': 'EUR',
  'en-NL': 'EUR',
  'en-DE': 'EUR',
  'en-IT': 'EUR',
  'en-ES': 'EUR',
  'en-FI': 'EUR',
  'en-GR': 'EUR',
  'en-AT': 'EUR',
  'en-BE': 'EUR',
  'en-CY': 'EUR',
  'en-EE': 'EUR',
  'en-LV': 'EUR',
  'en-LT': 'EUR',
  'en-LU': 'EUR',
  'en-MT': 'EUR',
  'en-SK': 'EUR',
  'en-SI': 'EUR',
  'en-MC': 'EUR',
  'en-ME': 'EUR',
  'en-SM': 'EUR',
  'en-AX': 'EUR',
  'en-AD': 'EUR',
  'en-VA': 'EUR',
  'en-BL': 'EUR',
  'en-MF': 'EUR',
  'en-PM': 'EUR',
  'en-RE': 'EUR',
  'en-YT': 'EUR',
  'en-GP': 'EUR',
  'en-MQ': 'EUR',
  'en-TF': 'EUR',
  'en-GF': 'EUR',
  'en-LS': 'ZAR',
  'en-NA': 'ZAR',
  'en-BT': 'INR',
  'en-AS': 'USD',
  'en-BQ': 'USD',
  'en-IO': 'USD',
  'en-EC': 'USD',
  'en-SV': 'USD',
  'en-GU': 'USD',
  'en-HT': 'USD',
  'en-MH': 'USD',
  'en-FM': 'USD',
  'en-MP': 'USD',
  'en-PW': 'USD',
  'en-PA': 'USD',
  'en-PR': 'USD',
  'en-TL': 'USD',
  'en-TC': 'USD',
  'en-UM': 'USD',
  'en-VG': 'USD',
  'en-VI': 'USD'
} as const;

type LanguageCode = typeof navigator.language;
type CurrencyCode = typeof localeToCurrency[LanguageCode];
type Response =
  | {
      code: CurrencyCode;
      locale: LanguageCode;
      error: undefined;
    }
  | {
      code: undefined;
      locale: LanguageCode;
      error: Error;
    };

const codeFromLocale = (locale: LanguageCode) => {
  if (localeToCurrency[locale]) {
    return {
      code: localeToCurrency[locale],
      locale,
      error: undefined
    };
  }

  // Try country code only
  const countryCode = locale.split('-')[1];
  if (countryCode) {
    for (const key in localeToCurrency) {
      if (key.endsWith(countryCode)) {
        return {
          code: localeToCurrency[key],
          locale,
          error: undefined
        };
      }
    }
  }

  // Try base language only
  const baseLang = locale.split('-')[0];
  for (const key in localeToCurrency) {
    if (key.startsWith(baseLang)) {
      return {
        code: localeToCurrency[key],
        locale,
        error: undefined
      };
    }
  }
  return {
    code: undefined,
    locale,
    error: new Error(`No currency code found for ${locale}`)
  };
};

// TODO use currency code helper from shared folder
export const useTravelCurrencyCode = withWindow(
  global => (locale: LanguageCode = global.navigator.language): Response => {
    return codeFromLocale(locale);
  },
  (locale?: LanguageCode) => {
    if (!locale) {
      return {
        code: undefined,
        locale: undefined,
        error: new Error('Must pass locale during SSR')
      };
    }
    return codeFromLocale(locale);
  }
);

// const dataFromNuitee = {
//   data: [
//     {
//       code: 'AED',
//       currency: 'UAE Dirham',
//       countries: ['AE']
//     },
//     {
//       code: 'AUD',
//       currency: 'Australian Dollar',
//       countries: ['AU', 'CX', 'CC', 'HM', 'KI', 'NR', 'NF', 'TV']
//     },
//     {
//       code: 'BRL',
//       currency: 'Brazilian Real',
//       countries: ['BR']
//     },
//     {
//       code: 'CAD',
//       currency: 'Canadian Dollar',
//       countries: ['CA']
//     },
//     {
//       code: 'CNY',
//       currency: 'Yuan Renminbi',
//       countries: ['CN']
//     },
//     {
//       code: 'EUR',
//       currency: 'Euro',
//       countries: [
//         'AX',
//         'AD',
//         'AT',
//         'BE',
//         'CY',
//         'EE',
//         'EU',
//         'FI',
//         'FR',
//         'GF',
//         'TF',
//         'DE',
//         'GR',
//         'GP',
//         'VA',
//         'IE',
//         'IT',
//         'LV',
//         'LT',
//         'LU',
//         'MT',
//         'MQ',
//         'YT',
//         'MC',
//         'ME',
//         'NL',
//         'PT',
//         'RE',
//         'BL',
//         'MF',
//         'PM',
//         'SM',
//         'SK',
//         'SI',
//         'ES'
//       ]
//     },
//     {
//       code: 'FJD',
//       currency: 'Fiji Dollar',
//       countries: ['FJ']
//     },
//     {
//       code: 'GBP',
//       currency: 'Pound Sterling',
//       countries: ['GG', 'IM', 'JE', 'GB']
//     },
//     {
//       code: 'HKD',
//       currency: 'Hong Kong Dollar',
//       countries: ['HK']
//     },
//     {
//       code: 'IDR',
//       currency: 'Rupiah',
//       countries: ['ID']
//     },
//     {
//       code: 'INR',
//       currency: 'Indian Rupee',
//       countries: ['BT', 'IN']
//     },
//     {
//       code: 'JPY',
//       currency: 'Yen',
//       countries: ['JP']
//     },
//     {
//       code: 'LKR',
//       currency: 'Sri Lanka Rupee',
//       countries: ['LK']
//     },
//     {
//       code: 'MAD',
//       currency: 'Moroccan Dirham',
//       countries: ['MA']
//     },
//     {
//       code: 'MNT',
//       currency: 'Tugrik',
//       countries: ['MN']
//     },
//     {
//       code: 'MUR',
//       currency: 'Mauritius Rupee',
//       countries: ['MU']
//     },
//     {
//       code: 'MXN',
//       currency: 'Mexican Peso',
//       countries: ['MX']
//     },
//     {
//       code: 'MYR',
//       currency: 'Malaysian Ringgit',
//       countries: ['MY']
//     },
//     {
//       code: 'OMR',
//       currency: 'Rial Omani',
//       countries: ['OM']
//     },
//     {
//       code: 'PHP',
//       currency: 'Philippine Peso',
//       countries: ['PH']
//     },
//     {
//       code: 'RUB',
//       currency: 'Russian Ruble',
//       countries: ['RU']
//     },
//     {
//       code: 'SAR',
//       currency: 'Saudi Riyal',
//       countries: ['SA']
//     },
//     {
//       code: 'SGD',
//       currency: 'Singapore Dollar',
//       countries: ['SG']
//     },
//     {
//       code: 'THB',
//       currency: 'Baht',
//       countries: ['TH']
//     },
//     {
//       code: 'TWD',
//       currency: 'New Taiwan Dollar',
//       countries: ['TW']
//     },
//     {
//       code: 'USD',
//       currency: 'US Dollar',
//       countries: ['AS', 'BQ', 'IO', 'EC', 'SV', 'GU', 'HT', 'MH', 'FM', 'MP', 'PW', 'PA', 'PR', 'TL', 'TC', 'UM', 'US', 'VG', 'VI']
//     },
//     {
//       code: 'ZAR',
//       currency: 'Rand',
//       countries: ['LS', 'NA', 'ZA']
//     }
//   ]
// };
